import { Suspense, useEffect } from 'react';
import { Box, CircularProgress, Link, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ListAltOutlined from '@mui/icons-material/ListAltOutlined';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Components } from 'react-markdown';
import { LazyMarkdown } from '@/components/LazyMarkdown';
import { CACHE_TIME } from '@/config/queryDefaults';
import { hasFormSchemaModule } from '@/lib/formSchemaModules';
import { helpApi, type HelpPage } from './helpApi';
import { HelpIndexView } from './HelpIndexView';
import { helpPanel, parseHelpLink, type HelpFieldTarget, type HelpPageTarget, type HelpTarget } from './helpPanelStore';

interface OpensHelp {
  language: string;
  onOpen: (target: HelpTarget) => void;
}

interface HelpForScreenProps extends OpensHelp {
  entity: string;
  /** The entity's module, as the page context names it; the field information needs it to find the form. */
  module?: string;
}

/**
 * The help for the entity on screen: "Show field information" when the entity has a form, then the help pages
 * that apply to it — with none it says so and shows the index.
 */
export function HelpForScreen({ entity, module, language, onOpen }: HelpForScreenProps) {
  return (
    <>
      {module && hasFormSchemaModule(module, entity) && (
        <FieldInformationEntry onClick={() => onOpen({ fieldsOf: entity, module })} />
      )}
      <ScreenHelpPages entity={entity} language={language} onOpen={onOpen} />
    </>
  );
}

/** The way to the fields of the screen's form with their help texts, which the panel shows only when asked to. */
function FieldInformationEntry({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <ListItemButton onClick={onClick} sx={{ mb: 2, gap: 1, py: 0.5, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
      <ListAltOutlined fontSize="small" color="action" />
      <ListItemText primary={t('help.showFields')} slotProps={{ primary: { variant: 'body2' } }} />
      <ChevronRight fontSize="small" color="action" />
    </ListItemButton>
  );
}

function ScreenHelpPages({ entity, language, onOpen }: HelpForScreenProps) {
  const { t } = useTranslation();
  const { data: pages, isLoading } = useQuery({
    queryKey: ['help', 'context', entity, language],
    queryFn: () => helpApi.context(entity),
    staleTime: CACHE_TIME.METADATA,
  });

  if (isLoading) return <CircularProgress size={20} />;
  if (!pages || pages.length === 0) {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('help.nothingForScreen')}
        </Typography>
        <HelpIndexView language={language} onOpen={onOpen} />
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {pages.map((page) => (
        <HelpPageLinks key={page.id} page={page} onOpen={onOpen} />
      ))}
      <Link component="button" variant="body2" onClick={helpPanel.browse} sx={{ alignSelf: 'flex-start' }}>
        {t('help.browseAll')}
      </Link>
    </Box>
  );
}

function HelpPageLinks({ page, onOpen }: { page: HelpPage; onOpen: (target: HelpTarget) => void }) {
  return (
    <Box>
      <Link component="button" variant="subtitle2" onClick={() => onOpen({ pageId: page.id })} sx={{ textAlign: 'left' }}>
        {page.title}
      </Link>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {page.summary}
      </Typography>
      <List dense disablePadding>
        {page.sections.map((section) => (
          <ListItemButton key={section.id} onClick={() => onOpen({ pageId: page.id, anchor: section.anchor })} sx={{ borderRadius: 1 }}>
            <ListItemText primary={section.heading} />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
}

/** Links in help text: `help:` opens the page in the panel, anything else opens in a new tab. */
const markdownComponents: Components = {
  a: ({ href, children }) => {
    const target = parseHelpLink(href);
    if (target) {
      return (
        <Link component="button" onClick={() => helpPanel.openTarget(target)} sx={{ verticalAlign: 'baseline' }}>
          {children}
        </Link>
      );
    }
    return (
      <Link href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </Link>
    );
  },
};

interface HelpFieldViewProps extends OpensHelp {
  target: HelpFieldTarget;
}

/** A field's help — the form tooltip's text — and the pages about the field's entity. */
export function HelpFieldView({ target, language, onOpen }: HelpFieldViewProps) {
  const { t } = useTranslation();
  const { data: field, isLoading, isError } = useQuery({
    queryKey: ['help', 'field', target.fieldId, language],
    queryFn: () => helpApi.field(target.fieldId),
    staleTime: CACHE_TIME.METADATA,
    retry: false,
  });
  const { data: pages } = useQuery({
    queryKey: ['help', 'context', field?.entity, language],
    queryFn: () => (field ? helpApi.context(field.entity) : Promise.resolve([])),
    enabled: !!field,
    staleTime: CACHE_TIME.METADATA,
  });

  if (isLoading) return <CircularProgress size={20} />;
  if (isError || !field) {
    return <Typography variant="body2" color="text.secondary">{t('help.unavailable')}</Typography>;
  }

  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {field.entityLabel}
      </Typography>
      <Typography variant="h6" component="h2">
        {field.label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
        {field.text}
      </Typography>
      {pages && pages.length > 0 && (
        <>
          <Typography variant="subtitle2">{t('help.pagesAbout', { entity: field.entityLabel })}</Typography>
          <List dense disablePadding>
            {pages.map((page) => (
              <ListItemButton key={page.id} onClick={() => onOpen({ pageId: page.id })} sx={{ borderRadius: 1 }}>
                <ListItemText primary={page.title} secondary={page.summary} />
              </ListItemButton>
            ))}
          </List>
        </>
      )}
    </Box>
  );
}

interface HelpPageViewProps {
  target: HelpPageTarget;
  language: string;
}

export function HelpPageView({ target, language }: HelpPageViewProps) {
  const { t } = useTranslation();
  const { data: page, isLoading, isError } = useQuery({
    queryKey: ['help', 'page', target.pageId, language],
    queryFn: () => helpApi.page(target.pageId),
    staleTime: CACHE_TIME.METADATA,
    retry: false,
  });

  useEffect(() => {
    if (page && target.anchor) {
      document.getElementById(sectionElementId(target.anchor))?.scrollIntoView({ block: 'start' });
    }
  }, [page, target.anchor]);

  if (isLoading) return <CircularProgress size={20} />;
  if (isError || !page) {
    return <Typography variant="body2" color="text.secondary">{t('help.unavailable')}</Typography>;
  }

  return <HelpPageContent page={page} />;
}

function HelpPageContent({ page }: { page: HelpPage }) {
  return (
    <Box
      sx={{
        fontSize: '0.875rem',
        '& p': { mt: 0, mb: 1 },
        // Tailwind's preflight strips list markers; numbered steps are the point of a task page.
        '& ul, & ol': { pl: 2.5, mt: 0, mb: 1 },
        '& ul': { listStyleType: 'disc' },
        '& ol': { listStyleType: 'decimal' },
        '& li': { mb: 0.5 },
      }}
    >
      <Typography variant="h6" component="h2">
        {page.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {page.summary}
      </Typography>
      {page.sections.map((section) => (
        <Box component="section" key={section.id} id={sectionElementId(section.anchor)} sx={{ mb: 2, scrollMarginTop: 8 }}>
          <Typography variant="subtitle1" component="h3" fontWeight={600} sx={{ mb: 0.5 }}>
            {section.heading}
          </Typography>
          <Suspense fallback={<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{section.markdown}</Typography>}>
            <LazyMarkdown components={markdownComponents} keepSchemes={['help:']}>
              {section.markdown}
            </LazyMarkdown>
          </Suspense>
        </Box>
      ))}
    </Box>
  );
}

const sectionElementId = (anchor: string) => `help-section-${anchor}`;
